/**
 * Step 4: Scope & Settings (Optional)
 *
 * Configure quiz difficulty and behavior:
 * - Scope: Group features by property (e.g., by State)
 * - Repetitions: How many times each prompt appears
 * - Max Attempts: How many guesses allowed per question
 */

import { useMemo } from "react";
import { HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useStudioStore,
  selectDataset,
  selectScopeConfig,
  selectPromptKeys,
  selectFilters,
  setScopeConfig,
} from "@/stores/useStudioStore";
import { countUniquePrompts, countFinalPrompts } from "@/utils/promptGrouping";
import { applyFilters, countFilteredFeatures } from "@/utils/filterUtils";

export function Step4ScopeSettings() {
  const dataset = useStudioStore(selectDataset);
  const scopeConfig = useStudioStore(selectScopeConfig);
  const promptKeys = useStudioStore(selectPromptKeys);
  const filters = useStudioStore(selectFilters);
  const excludedFeatureIds = useStudioStore((state) => state.excludedFeatureIds);

  // Get available properties for scope grouping
  const availableProperties = dataset.collection
    ? Array.from(
        new Set(
          dataset.collection.features.flatMap((f) =>
            f.properties ? Object.keys(f.properties) : []
          )
        )
      ).sort()
    : [];

  const handleScopeChange = (value: string) => {
    setScopeConfig({
      ...scopeConfig,
      scopeKey: value === "none" ? null : value,
    });
  };

  const handleRepetitionsChange = (value: number[]) => {
    setScopeConfig({
      ...scopeConfig,
      repetitions: value[0] || 1,
    });
  };

  const handleMaxAttemptsChange = (value: number[]) => {
    setScopeConfig({
      ...scopeConfig,
      maxAttempts: value[0] || 1,
    });
  };

  // Calculate correct feature and question counts (memoized)
  const { finalFeatureCount, questionCount } = useMemo(() => {
    if (!dataset.collection) {
      return { finalFeatureCount: 0, questionCount: 0 };
    }

    // Apply filters to get filtered collection
    const filteredCollection = applyFilters(dataset.collection, filters);

    // Count features after filters and exclusions
    const filteredCount = countFilteredFeatures(dataset.collection, filters);
    const final = filteredCount - excludedFeatureIds.length;

    // Count unique prompts (handles grouping by shared values)
    const prompts = countFinalPrompts(filteredCollection, promptKeys, excludedFeatureIds);
    const questions = prompts * scopeConfig.repetitions;

    return {
      finalFeatureCount: final,
      questionCount: questions,
    };
  }, [dataset.collection, filters, excludedFeatureIds, promptKeys, scopeConfig.repetitions]);

  if (!dataset.collection) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <p>No dataset loaded. Please go back to Step 1.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Scope & Settings (Optional)</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Control quiz difficulty and behavior
            </p>
          </div>
          <Badge variant="outline">Optional</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Scope */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="scope-select">Scope Grouping</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="inline-flex items-center">
                    <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <div className="space-y-2">
                    <p className="font-medium">Group questions by geographic hierarchy</p>
                    <p className="text-xs">
                      When enabled, questions are organized by their parent region. Only features within the same group are clickable at once, and you'll work through one region before moving to the next.
                    </p>
                    <div className="space-y-1 text-xs">
                      <p className="text-muted-foreground">
                        <strong>Best for:</strong> Learning one region at a time, building mental maps of geographic relationships
                      </p>
                      <p className="text-muted-foreground">
                        <strong>Avoid if:</strong> You prefer fully random order, or are testing well-known material
                      </p>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select
            value={scopeConfig.scopeKey || "none"}
            onValueChange={handleScopeChange}
          >
            <SelectTrigger id="scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">All regions clickable (no scope)</SelectItem>
              {availableProperties.map((prop) => (
                <SelectItem key={prop} value={prop}>
                  Group by {prop}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {scopeConfig.scopeKey
              ? `Only features in the same ${scopeConfig.scopeKey} group will be clickable at once.`
              : "All features will be clickable throughout the quiz."}
          </p>

          {/* Example when scope grouping is enabled */}
          {scopeConfig.scopeKey && (
            <div className="mt-2 p-3 bg-muted rounded-lg text-xs">
              <div className="font-medium mb-1.5">Example quiz flow:</div>
              <div className="space-y-1 text-muted-foreground">
                <div>📍 Group 1 ({scopeConfig.scopeKey}) → All questions for this {scopeConfig.scopeKey}</div>
                <div>📍 Group 2 ({scopeConfig.scopeKey}) → All questions for this {scopeConfig.scopeKey}</div>
                <div>📍 Group 3 ({scopeConfig.scopeKey}) → And so on...</div>
              </div>
              <p className="text-muted-foreground mt-2 text-[11px] italic">
                Questions within each group are randomized, but groups appear sequentially.
              </p>
            </div>
          )}
        </div>

        {/* Repetitions */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Repetitions</Label>
            <Badge variant="secondary">{scopeConfig.repetitions}x</Badge>
          </div>
          <Slider
            value={[scopeConfig.repetitions]}
            onValueChange={handleRepetitionsChange}
            min={1}
            max={5}
            step={1}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            Each prompt will appear {scopeConfig.repetitions} time(s) in the quiz.
            {scopeConfig.repetitions > 1 && " Great for reinforcement!"}
          </p>
        </div>

        {/* Max Attempts */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Max Attempts Per Question</Label>
            <Badge variant="secondary">{scopeConfig.maxAttempts}</Badge>
          </div>
          <Slider
            value={[scopeConfig.maxAttempts]}
            onValueChange={handleMaxAttemptsChange}
            min={1}
            max={5}
            step={1}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            You'll have {scopeConfig.maxAttempts} attempt(s) to answer each question correctly.
            {scopeConfig.maxAttempts === 1 && " One shot!"}
            {scopeConfig.maxAttempts > 3 && " Very forgiving!"}
          </p>
        </div>

        {/* Summary */}
        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
          <h4 className="text-sm font-semibold mb-2">Configuration Summary</h4>
          <ul className="space-y-1 text-xs text-muted-foreground">
            <li>
              • <strong>Features:</strong> {finalFeatureCount}
              {(filters.length > 0 || excludedFeatureIds.length > 0) && (
                <span className="text-[11px]"> (after filters & exclusions)</span>
              )}
            </li>
            <li>
              • <strong>Questions:</strong> {questionCount}
              {scopeConfig.repetitions > 1 && ` (${scopeConfig.repetitions}x repetition)`}
            </li>
            <li>
              • <strong>Scope:</strong>{" "}
              {scopeConfig.scopeKey ? `Grouped by ${scopeConfig.scopeKey}` : "All clickable"}
            </li>
            <li>
              • <strong>Attempts:</strong> {scopeConfig.maxAttempts} per question
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
